import InstanceFactory from "../src/factories/InstanceFactory";
import Assets from "./utils/Assets";
import Utils from "./utils/Utils";
import Locale from "./utils/Locale";
import { ILayoutSettings } from "./baseConfigs/layoutConfigShema";
import { Timeline } from "./Timeline";
import ParticleLoop from "./utils/ParticleLoop";
import IAppGameConfig from './model/IAppGameConfig';
import GameModel from "./model/GameModel";
import ConsoleUtil from './utils/ConsoleUtil';
import { gsap } from "gsap/dist/gsap";
import KeyboardManager, { Key } from "./utils/KeyboardManager";
import { Application, Renderer, DisplayObject, InteractionManager, utils as PixiUtils, settings as PixiSettings, Ticker, SCALE_MODES, IRenderingContext, MIPMAP_MODES } from "pixi.js";
import EventEmitter = PixiUtils.EventEmitter;
import ClipboardUtils from "./utils/ClipboardUtils";
import GameEvents from "./const/GameEvents";
import Cursor from "./views/components/Cursor";

export default class App extends Application {

    // TODO EXTRACT THOSE FROM HERE
    static version: string;
    // static layout;
    // static config: IAppGameConfig;
    static renderer: Renderer;
    static assets: Assets;
    static utils: Utils;
    static gameFactory;
    static dispatcher: EventEmitter;
    static locale;

    static lastTime:number;
    static dTime:number;
    static downTime:number = 0;

    static instance: App;
    static width: number;
    static height: number;
    static timeline: Timeline;
    static registeredOnLoop: {(time:number):void}[];
    private fpsBuffer: number[];
    private fpsBufferLength: number;
    private fpsAvg: number;
    timeScale: number;
    private _paused: boolean;
    debugBounds: boolean;
    private gl: IRenderingContext;
    private realDrawElements: Function;
    private realWebGLClear: Function;
    private drawCalls: number;
    private frameStartTime: number;
    private lastFrameTimeInMilliseconds: number;

    constructor(settingsConfig: ILayoutSettings, options?: any) {
        if (App.instance) {
            return App.instance;
        }

        const START_WIDTH = settingsConfig.nativeWidth;
        const START_HEIGHT = settingsConfig.nativeHeight;
        App.width = START_WIDTH;
        App.height = START_HEIGHT;
        const vpWidth = window.innerWidth;
        const vpHeight = window.innerHeight;
        let maxDPI = 2;
        if (vpWidth > START_WIDTH || vpHeight > START_HEIGHT ) {
            maxDPI = 1;
        }

        let dpr = Math.min(window.devicePixelRatio || 1 , maxDPI);
        // const dpr = window.devicePixelRatio || 1;
        // const dpr = 1;
        const lowRes = window.screen.width * window.screen.height * dpr <= 1280 * 720;

        ConsoleUtil.log("[App] Setup stage");

        // Set width and height to 10 to force resize - fix for Samsung browser
        // super({width: 10, height: 10, transparent: true, resolution: dpr, roundPixels: lowRes });
        super({width: 10, height: 10, backgroundAlpha: 0, resolution: Math.max(1, dpr), sharedLoader: true });

        //     this.dispatcher = new EventEmitter();
        App.instance = this;
        PixiSettings.SCALE_MODE = SCALE_MODES.LINEAR;
        PixiSettings["FAIL_IF_MAJOR_PERFORMANCE_CAVEAT"] = false;
        PixiSettings.MIPMAP_TEXTURES = MIPMAP_MODES.ON;
        // Fixes the WebGL flickering and as result missing splash overlay on Samsung Galaxy S22 (Android 12)
        PixiSettings.CAN_UPLOAD_SAME_BUFFER = false;

        // }
        App.locale = Locale;
        App.gameFactory = InstanceFactory;


        App.instance.ticker.add((time) => {
            App.gameLoopFunction(time);
        });
        // App.layout = Layout;
        // App.instance.ticker.lastTime

        this.timeScale = this.computeTimeScale();
        this.changeTimeScale();

        this.paused = false;
        this.debugBounds = false;
        var debug = process.env.NODE_ENV !== 'production';
        if (debug) {
            KeyboardManager.onKey("pressed", Key.PAUSE, () => {
                this.paused = !this.paused;
            }, this);

            this.debugBounds = GameModel.debugBounds;
            KeyboardManager.onKey("pressed", Key.B, () => {
                this.debugBounds = !this.debugBounds;
                App.dispatcher.emit(GameEvents.UI_UPDATE);
            }, this);
            KeyboardManager.onKey("pressed", Key.LEFT, () => {
                const step = KeyboardManager.isDown(Key.SHIFT) ? 0.01 : KeyboardManager.isDown(Key.CTRL) ? 0.5 : 0.05;
                this.changeTimeScale(Math.round(100 * (this.timeScale - step)) / 100);
            });
            KeyboardManager.onKey("pressed", Key.RIGHT, () => {
                const step = KeyboardManager.isDown(Key.SHIFT) ? 0.01 : KeyboardManager.isDown(Key.CTRL) ? 0.5 : 0.05;
                this.changeTimeScale(Math.round(100 * (this.timeScale + step)) / 100);
            });
        }

        App.timeline = new Timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true
        });

        // Prevent touch move, directly from PIXI
        // in order to make the swipe spin works on iOS 15
        let interactionManager = this.renderer.plugins.interaction as InteractionManager;
        if (interactionManager.supportsTouchEvents) {
            interactionManager.on("touchmove", function(ev) {
                // console.log("Preventing touch move");
                ev.data.originalEvent.preventDefault();
            });
        }

        Error.stackTraceLimit = Math.max(Error.stackTraceLimit, 50);

        // Log unhandled errors
        window.onerror = function(message, source, lineno, colno, error) {
            if (
                !GameModel.gameConfig.hasOwnProperty("debugLogging") ||
                !GameModel.gameConfig.debugLogging.hasOwnProperty(
                    "errorTrace"
                ) ||
                (GameModel.gameConfig.debugLogging.hasOwnProperty(
                    "errorTrace"
                ) &&
                    GameModel.gameConfig.debugLogging.errorTrace === true)
            ) {
                console.trace();
            }


            if (process.env.NODE_ENV !== 'production') {
                let copied = false;
                try {
                    ClipboardUtils.copyToClipboard(error.stack);
                    copied = true;
                } catch (e) {
                    copied = false;
                }

                alert(`Unhandled error occured${copied ? "(copied to clipboard)" : ""}!\n\nMessage: ${message}\n\nStack: ${error.stack}`);
            }
        };

        this.fpsBuffer = [];
        this.fpsAvg = 60;
        this.fpsBufferLength = 180;
        this.drawCalls = 0;
        this.lastFrameTimeInMilliseconds = Date.now();
        if (App.config.hasCustomMouseCursor) {
            this.addCustomMouseCursor();
        }
        globalThis.__PIXI_APP__ = this;

        this.gl = (App.instance.renderer as Renderer)?.gl;
        if (this.gl) {
            // @ts-ignore
            this.realDrawElements = this.gl.__proto__.drawElements;
            // @ts-ignore
            this.realWebGLClear = this.gl.__proto__.clear;

            // @ts-ignore
            this.gl.__proto__.drawElements = this.fakeDrawElements.bind(this);
            // @ts-ignore
            this.gl.__proto__.clear = this.fakeWebGLClear.bind(this);
        }
    }
    
    changeTimeScale(newTimeScale: number = this.timeScale) {
        if (newTimeScale <= 0) {
            return;
        }

        this.timeScale = newTimeScale;
        this.ticker.speed = this.timeScale;
        Ticker.shared.speed = this.timeScale;
        gsap.globalTimeline.timeScale(this.timeScale);
        ConsoleUtil.logWithColor(`TimeScale changed to ${this.timeScale}`, 0xff0000);
    }

    /**
     * Gets the last frame time, measured in milliseconds.
     * This is the time needed to draw a single frame on the pixi webgl canvas.
     *
     * @readonly
     * @type {number}
     * @memberof App
     */
    public get frameTime(): number {
        return this.lastFrameTimeInMilliseconds || 1;
    }

    private fakeDrawElements(mode: number, count: number, type: number, offset: number): void {
        this.drawCalls++;
        this.realDrawElements.call(this.gl, mode, count, type, offset);
    }

    private fakeWebGLClear(bitmask: number): void {
        if (bitmask == 16640) {
            const now = Date.now();
            if (this.frameStartTime) {
                this.lastFrameTimeInMilliseconds = now - this.frameStartTime;
            }

            this.frameStartTime = now;
            
            App.dispatcher.emit(GameEvents.DRAW_CALLS_COMPUTED, this.drawCalls);
            this.drawCalls = 0;
        }

        this.realWebGLClear.call(this.gl, bitmask);
    }

    private addCustomMouseCursor() {
        Cursor.init(this.renderer.plugins.interaction.cursorStyles, App.config.hasCustomMouseCursor);
    }
    public get paused() {
        return this._paused;
    }

    // App.instanse.paused
    public set paused(value: boolean) {
        this._paused = value;

        // This is not an effictive way to pause all spines.
        // TODO: use spine.state.timeScale property!
        // this.stage?.children?.forEach((l: Layer) => {
        //     l.getCurrentScene()?.spineAnimations?.forEach(s => value ? s.pause() : s.resume())
        // })

        this.ticker.speed = this.timeScale * (this._paused ? 0 : 1);
        Ticker.shared.speed = this.ticker.speed;
        gsap.globalTimeline.timeScale(this.ticker.speed);

    }

    static get config(): IAppGameConfig {
        return GameModel.gameConfig;
    }

    static set config(cg: IAppGameConfig) {
        ConsoleUtil.warn("REMOVE CONFIG SETTING FROM INDEX.TS");
    }

    static isObjectDestroyed(object: DisplayObject): boolean {
        if (object) {
            if ('_destroyed' in object) {
                return (<any>object)._destroyed;
            }

            throw new Error("DisplayObject does not have '_destroyed' property anymore!");
        }

        throw new Error("object can not be null!");
    }

    /**
     * Gets the average frames per second
     *
     * @readonly
     * @type {number}
     * @memberof App
     */
    public get averageFPS(): number {
        return this.fpsAvg;
    }

    private computeTimeScale(): number {
        let parsePositiveNumber = (val) => {
            if (val) {
                try {
                    let number = Number(val);
                    if (number > 0) {
                        return number;
                    }
                } catch (error) {
                }
            }

            return undefined;
        }

        let timeScaleFromUrlParam = parsePositiveNumber(GameModel.timeScale);
        if (timeScaleFromUrlParam) {
            return timeScaleFromUrlParam;
        }

        let timeScaleFromConfig = parsePositiveNumber(GameModel.gameConfig.timeScale);
        if (timeScaleFromConfig) {
            return timeScaleFromConfig;
        }

        return 1;
    }

    private static gameLoopFunction(time: number) {
        //TODO all game updates should happen here
        if (!App.timeline.isActive() && App.timeline.getChildren().length ) {
            // FIX FOR GLOBAL TIMELINE DISRUPTION
            ConsoleUtil.warn("[TIMELINE STATUS DISRUPTED]:", App.timeline.paused, App.timeline.isActive(), App.timeline.getChildren());
            App.timeline.play();
        }
        ParticleLoop.update(time);

        this.dTime = this.lastTime? App.instance.ticker.lastTime - this.lastTime:0;
        if(this.dTime>App.instance.ticker.deltaTime) {
            // debugger;
            // this.downTime += this.dTime;
        }
        this.downTime += this.dTime - time;
    // if(this.dTime>1000){
    //     debugger;
    // }
        this.lastTime = App.instance.ticker.lastTime;
        if(App.registeredOnLoop) {
            App.registeredOnLoop.forEach(callback=>{
                callback(this.getActualTime());
            })
        }

        App.instance.fpsBuffer.push(App.instance.ticker.FPS);
        if (App.instance.fpsBuffer.length >= App.instance.fpsBufferLength) {
            App.instance.fpsAvg = App.instance.fpsBuffer.reduce((prev, curr) => prev + curr, 0) / App.instance.fpsBuffer.length;
            App.instance.fpsBuffer = [];
        }
    }

    public static getActualTime():number {
        // return App.instance.ticker.lastTime - this.downTime;
        return (App.instance.ticker.lastTime - this.downTime)/100;
    }

    public static subscribeToGameLoop(callBack:(...any)=>void) {
        if(!App.registeredOnLoop){
            App.registeredOnLoop = [];
        }

        if(!App.registeredOnLoop.includes(callBack)){
            App.registeredOnLoop.push(callBack);
        }

    }

    public static unsubscribeToGameLoop(callBack:(...any)=>void) {
        if(App.registeredOnLoop && App.registeredOnLoop.includes(callBack)){
            App.registeredOnLoop.splice(App.registeredOnLoop.indexOf(callBack),1);
        }
    }

/**
 *
 * @param display {DisplayObject}
 * @param callback {Function}
 * @param args {*[]=}
 * @param context {*=}
 */
    static addedToStage(display, callback, args = null, context = null) {
        while (display.parent) {
            display = display.parent;
        }
        if (display === App.instance.stage) {
            callback.apply(context, args);
        } else {
            display.once(
                "added",
                () => {
                    App.addedToStage(display, callback, args, context);
                },
                this
            );
        }
    }


}
